"use client";

import React, { useState } from 'react';
import Image from "next/legacy/image";
import { Modal } from '@/components/atoms/modal';
import { Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext } from '@/components/atoms/carousel';

interface GaleriaImage {
    url?: string;
    alternativeText?: string;
}

interface EventoGalleryProps {
    galeria: GaleriaImage[];
    title?: string;
}

const EventoGallery: React.FC<EventoGalleryProps> = ({ galeria, title = 'Galeria de Fotos' }) => {
    // Normalizar dados
    const galeriaImages = Array.isArray(galeria)
        ? galeria
            .filter((img): img is { url: string; alternativeText?: string } => typeof img?.url === 'string' && !!img.url)
            .map(img => ({
                url: img.url as string,
                alt: img.alternativeText || 'Imagem da galeria'
            }))
        : [];

    const [isGalleryModalOpen, setIsGalleryModalOpen] = useState(false);
    const [selectedImageIndex, setSelectedImageIndex] = useState(0);
    const [imageLoadingStates, setImageLoadingStates] = useState<{ [key: number]: boolean }>({});

    if (!galeriaImages || galeriaImages.length === 0) return null;

    const openGalleryModal = (index: number = 0) => {
        setSelectedImageIndex(index);
        setIsGalleryModalOpen(true);
    };

    const closeGalleryModal = () => {
        setIsGalleryModalOpen(false);
        setSelectedImageIndex(0);
    };

    const handleImageLoad = (index: number) => {
        setImageLoadingStates(prev => ({ ...prev, [index]: false }));
    };

    return (
        <section className='container mx-auto px-4 sm:px-6 lg:px-8 lg:my-40'>
            <div className="flex items-center justify-between mb-6 sm:mb-8 lg:mb-10">
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold text-left font-exo-2">{title}</h2>
                <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                    {galeriaImages.length} {galeriaImages.length === 1 ? 'foto' : 'fotos'}
                </span>
            </div>

            <Carousel className="w-full">
                <CarouselContent className="gap-4">
                    {galeriaImages.map((image, index) => (
                        <CarouselItem
                            key={index}
                            className={index % 2 === 0 ? 'basis-auto shrink-0' : 'basis-auto shrink-0'}
                        >
                            <button
                                type="button"
                                onClick={() => openGalleryModal(index)}
                                className={
                                    index % 2 === 0
                                        ? 'group block w-[85vw] sm:w-[75vw] md:w-[65vw] lg:w-[460px] h-56 sm:h-64 md:h-72 lg:h-80 rounded-xl overflow-hidden bg-gray-50 hover:shadow-lg transition'
                                        : 'group block w-[65vw] sm:w-[55vw] md:w-[45vw] lg:w-56 h-40 sm:h-48 md:h-56 lg:h-80 rounded-xl overflow-hidden bg-gray-50 hover:shadow-lg transition'
                                }
                                aria-label={`Ver imagem ${index + 1} de ${galeriaImages.length}: ${image.alt}`}
                                title={`Clique para ampliar - ${image.alt}`}
                            >
                                <div className="relative w-full h-full">
                                    {imageLoadingStates[index] && (
                                        <div className="absolute inset-0 bg-gray-200 animate-pulse" />
                                    )}
                                    <Image
                                        src={image.url}
                                        alt={image.alt}
                                        layout="fill"
                                        className="object-cover"
                                        onLoadingComplete={() => handleImageLoad(index)}
                                    />
                                </div>
                            </button>
                        </CarouselItem>
                    ))}
                </CarouselContent>
                <CarouselPrevious className="-left-3 top-1/2 -translate-y-1/2" aria-label="Anterior" />
                <CarouselNext className="-right-3 top-1/2 -translate-y-1/2" aria-label="Seguinte" />
            </Carousel>

            {/* Modal */}
            <Modal
                isOpen={isGalleryModalOpen}
                onClose={closeGalleryModal}
                title={`Galeria de Fotos (${selectedImageIndex + 1}/${galeriaImages.length})`}
                maxWidth="6xl"
            >
                <div className="p-4">
                    <Carousel
                        className="w-full"
                        opts={{
                            startIndex: selectedImageIndex,
                            align: "center",
                            loop: galeriaImages.length > 1
                        }}
                    >
                        <CarouselContent>
                            {galeriaImages.map((image, index) => (
                                <CarouselItem key={index}>
                                    <div className="relative aspect-video w-full max-h-[70vh] bg-gray-100 rounded-lg overflow-hidden">
                                        <Image
                                            src={image.url}
                                            alt={image.alt}
                                            layout="fill"
                                            className="object-contain"
                                            sizes="90vw"
                                            quality={95}
                                        />
                                    </div>
                                    <div className="text-center mt-4 space-y-2">
                                        <p className="text-gray-600">
                                            {index + 1} de {galeriaImages.length}
                                        </p>
                                        <p className="text-sm text-gray-500 max-w-md mx-auto">
                                            {image.alt}
                                        </p>
                                    </div>
                                </CarouselItem>
                            ))}
                        </CarouselContent>
                        <CarouselPrevious className="absolute left-4 top-1/2 bg-white/90 hover:bg-white shadow-lg" />
                        <CarouselNext className="absolute right-4 top-1/2 bg-white/90 hover:bg-white shadow-lg" />
                    </Carousel>
                </div>
            </Modal>
        </section>
    );
};

export default EventoGallery;
